import { FrontSide } from 'three';

import glslConst from '@resn/gozer-glsl/math/const.glsl';
import { CustomMaterial } from '@resn/gozer-three/materials';

import {
    shaderChunks as glslMatcap,
    uniforms as glslMatcapUniforms,
} from '@/components/gl/shaders/ShaderChunks/matcap';
import { shaderChunks as glslPeekShock } from '@/components/gl/shaders/ShaderChunks/peek-shock';
import { shaderChunks as glslPreReveal } from '@/components/gl/shaders/ShaderChunks/pre-reveal';
import { shaderChunks as glslPreRevealIrri } from '@/components/gl/shaders/ShaderChunks/pre-reveal-irridecent';
import { shaderChunks as glslRawPreReveal } from '@/components/gl/shaders/ShaderChunks/raw-pre-reveal';
import {
    shaderChunks as glslShineWipe,
    uniforms as glslShineWipeUniforms,
} from '@/components/gl/shaders/ShaderChunks/shine-wipe';
import { shaderChunks as glslShine } from '@/components/gl/shaders/ShaderChunks/transitions/shine';
import { shaderChunks as glslWipe } from '@/components/gl/shaders/ShaderChunks/wipe-reveal';
import glslBlend from '@/libs/lygia/color/blend.glsl';
import glslFill from '@/libs/lygia/draw/fill.glsl';

const unwrapUniformValues = (uniforms) =>
    Object.fromEntries(
        Object.entries(uniforms).map(([key, uniform]) => {
            if (
                uniform &&
                typeof uniform === 'object' &&
                Object.prototype.hasOwnProperty.call(uniform, 'value')
            ) {
                return [key, uniform.value];
            }

            return [key, uniform];
        })
    );

const vertexShader = /* glsl */ `
    varying vec3 vNormal;
    varying vec3 vNormalW;
    varying vec3 vViewPosition;
    varying vec2 vUv;
    varying vec3 vWorldPosition;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vNormalW = normalize(mat3(modelMatrix) * normal);
        vPosition = position;

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = mvPosition.xyz;
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;

        gl_Position = projectionMatrix * mvPosition;
    }
`;

const fragmentShader = /* glsl */ `
    varying vec3 vNormal;
    varying vec3 vNormalW;
    varying vec3 vViewPosition;
    varying vec2 vUv;
    varying vec3 vWorldPosition;
    varying vec3 vPosition;

    uniform sampler2D tMap;

    ${glslConst}
    ${glslBlend}
    ${glslFill}

    ${glslShine.uniforms}
    ${glslShine.funcs}
    ${glslShineWipe.uniforms}

    ${glslMatcap.uniforms}
    ${glslMatcap.funcs}
    ${glslPreReveal.funcs}
    ${glslPreRevealIrri.funcs}
    ${glslRawPreReveal.funcs}
    ${glslPeekShock.funcs}
    ${glslWipe.funcs}
    ${glslShineWipe.funcs}

    uniform float progress;
    uniform float shinePosition;
    uniform float alpha;
    uniform float uOutlineScale;
    uniform float uTime;
    uniform float uShineZOffset;
    uniform float uIrriStartAngle;

    uniform float u_noiseScaleX;
    uniform float u_noiseScaleY;
    uniform float u_noiseMag;
    uniform float u_noisePanSpeed;
    uniform float u_diffuseMix;
    uniform float u_chromeOpacity;
    uniform float u_stackDarken;

    uniform sampler2D tMatcapGloss;
    uniform sampler2D tMatcapIrri;
    uniform sampler2D uNoise;
    uniform sampler2D uChromeNoise;

    void main() {
        vec3 normal = normalize(vNormal);

        vec4 preRevealOutput;
        vec4 mainOutput = texture2D(tMap, vUv);
        vec4 finalColor;

        #ifdef USE_PRE_REVEAL
            float irriStartAngle = uIrriStartAngle + uTime * 0.5;
            vec3 irriColor = preRevealIrri(vUv, uOutlineScale, tMatcapIrri, normal, vViewPosition, irriStartAngle, u_matcapScale);
            // vec3 preRevealRawColor = preRevealRaw(vUv, uNoise, uTime);
            // vec3 gold = vec3(1.0, 0.82, 0.22);
            // preRevealOutput.rgb = mix(gold * 0.1, gold, preRevealRawColor.r);
            preRevealOutput.rgb = irriColor;
       
            preRevealOutput.a = 1.0;

            // preRevealOutput.a = 1.0;
        #endif

        vec4 outWipe;
        #ifdef USE_TRANSITION_WIPE
            outWipe = wipe(vUv, uWipeAngle, uWipeRevealPr, uPreRevealColor2, uPreRevealColor1, uPreRevealColor2, uNoise, uTime);
        #endif

        #ifdef USE_MATCAP_GLOSS
            vec3 cMatcapGloss = matcap(tMatcapGloss, normal, vViewPosition, false, 1.0, 1.0, 0., 0., u_matcapScale);
            mainOutput.rgb = blendScreen(mainOutput.rgb, cMatcapGloss, 0.25);
        #endif

        #ifdef USE_CHROME_SHIMMER
            vec3 csVN = normalize(vNormalW);

            vec2 noiseUV = vec2(
                vUv.x * u_noiseScaleX + csVN.x * u_noiseMag,
                vUv.y * u_noiseScaleY + csVN.y * u_noiseMag + uTime * u_noisePanSpeed
            );
            float n = texture2D(uChromeNoise, noiseUV).r;

            float cardLuma = dot(finalColor.rgb, vec3(0.299, 0.587, 0.114));
            float smoothness = mix(1.0, cardLuma, u_diffuseMix);

            vec3 chromeColor = mix(vec3(0.22, 0.22, 0.26), vec3(0.95, 0.95, 1.0), n);

            mainOutput.rgb = 1.0 - (1.0 - mainOutput.rgb) * (1.0 - chromeColor * u_chromeOpacity * smoothness);
            // finalColor.rgb = mix(finalColor.rgb, vec3(0.0), u_stackDarken);
            // finalColor.rgb = vec3(n);
        #endif

        bool useShineWipe = true;
        if (useShineWipe) {
            vec3 shineWipeColor = shineWipe(mainOutput.rgb, vUv, uShineWipePr, uShineWipeAngle, uShineWipeThickness);
            mainOutput.rgb = shineWipeColor;
        }

        #ifdef USE_TRANSITION_IN
            mainOutput.rgb = mix(vec3(0.0), mainOutput.rgb, progress);
        #endif

        #ifdef USE_TRANSITION_SHINE
            mainOutput.rgb = transition_shine(mainOutput.xyz, vWorldPosition, 0.0, 0.0, uShineZOffset);
        #endif

        mainOutput.a *= alpha;

        #ifdef USE_PRE_REVEAL
            #ifdef USE_TRANSITION_WIPE
                finalColor = mix(preRevealOutput, mainOutput, outWipe.a);
                finalColor.rgb = blendScreen(finalColor.rgb, outWipe.rgb);
            #else
                finalColor = mainOutput;
            #endif
        #else
            finalColor = mainOutput;
        #endif

        finalColor.rgb = applyPeekShock(finalColor.rgb, vWorldPosition);

        gl_FragColor = finalColor;

        #ifdef USE_DEBUG
            #ifdef USE_CHROME_SHIMMER
                gl_FragColor = vec4(vec3(n), 1.0);
            #endif
            #ifdef USE_MATCAP_GLOSS
                gl_FragColor = vec4(cMatcapGloss, 1.0);
            #endif
        #endif
    }
`;

class CardRawMaterial2 extends CustomMaterial {
    constructor(props = {}) {
        super({
            options: {
                name: 'CardRawMaterial',
                transparent: false,
                side: FrontSide,
                ...props,
            },
            uniforms: unwrapUniformValues({
                tMap: null,
                progress: 0,
                shinePosition: 0,
                alpha: 1,

                uOutlineScale: 0,
                uTexPreRevealSticker: null,
                uTime: 0,
                uNoise: null,
                uChromeNoise: null,
                uShineZOffset: 0,

                u_noiseScaleX: 0.75,
                u_noiseScaleY: 0.4,
                u_noiseMag: 1.75,
                u_noisePanSpeed: 0.1,
                u_diffuseMix: 0.9,
                u_chromeOpacity: 1,
                u_stackDarken: 0.0,

                tMatcapGloss: null,
                tMatcapIrri: null,
                ...glslMatcapUniforms(),
                ...glslPreReveal.uniforms(),
                ...glslPreRevealIrri.uniforms(),
                ...glslRawPreReveal.uniforms(),
                ...glslPeekShock.uniforms(),
                ...glslWipe.uniforms(),
                ...glslShineWipeUniforms(),

                uHeartbeatPr: 1,
                uIrriStartAngle: Math.random() * Math.PI * 2,
            }),
            vs: vertexShader,
            fs: fragmentShader,
        });

        this.defines = {
            USE_UV: true,
            USE_TRANSITION_IN: true,
            USE_PRE_REVEAL: true,
            USE_TRANSITION_WIPE: true,
            USE_TRANSITION_SHINE: false,
            USE_MATCAP_GLOSS: false,
            USE_CHROME_SHIMMER: true,
            ...props.defines,
        };
    }

    set map(value) {
        this.uniforms.tMap.value = value;
    }

    get map() {
        return this.uniforms.tMap.value;
    }
}

export default CardRawMaterial2;
